import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Check, ChevronDown } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { cn } from "@/shared/lib/utils";
import { Input } from "@/shared/components/ui/Input";

/**
 * Searchable single-select. Trigger matches the Input/SelectInput surface
 * (glass-surface-subtle, rounded-lg, h-10); the floating panel uses the
 * mid-tier glass like SelectInput's content (design.md §8–9). Built on Radix
 * Popover so it stays a `ui/` primitive — raw `<button>` is allowed here.
 *
 * Filtering is client-side substring match on the option label, suitable for a
 * few hundred options (e.g. the country list) without a virtualized list.
 */
export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-label"?: string;
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No matches",
  disabled,
  className,
  id,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "aria-label": ariaLabel,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  function select(next: string) {
    onValueChange(next);
    setOpen(false);
    setQuery("");
  }

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setQuery("");
  }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          aria-label={ariaLabel}
          className={cn(
            "inline-flex h-10 w-full items-center justify-between gap-2",
            "px-4 type-body-base text-left",
            "rounded-lg border border-border",
            "glass-surface-subtle",
            selected ? "text-text" : "text-text-muted",
            "focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            className,
          )}
        >
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          // Focus the filter input on open rather than the first item.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
          className={cn(
            "z-dropdown overflow-hidden",
            "rounded-xl glass-surface shadow-lg",
            "w-[var(--radix-popover-trigger-width)]",
          )}
        >
          <div className="p-2">
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filtered[0]) {
                  e.preventDefault();
                  select(filtered[0].value);
                }
              }}
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 type-body-sm text-text-muted">{emptyText}</p>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => select(opt.value)}
                  className={cn(
                    "relative flex w-full select-none items-center gap-2",
                    "rounded-lg py-2 pl-8 pr-3 type-body-base text-text text-left",
                    "outline-none cursor-pointer",
                    "hover:bg-surface-muted/40 focus-visible:bg-surface-muted/40",
                  )}
                >
                  {opt.value === value ? (
                    <span className="absolute left-2 inline-flex h-4 w-4 items-center justify-center">
                      <Check className="h-3 w-3 text-brand" aria-hidden />
                    </span>
                  ) : null}
                  <span className="truncate">{opt.label}</span>
                </button>
              ))
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
